// Third-party analytics / chat loaders, env-gated so they only run in
// production builds where the IDs are configured. Dev (`vite dev`) and
// any environment that leaves the vars unset (staging/preview) get a
// clean no-op — nothing is requested from Meta or Crisp.
//
// The IDs live in `.env.production` (loaded by `vite build`), mirroring
// how the Stripe + Maps keys are pinned. To turn tracking off on a
// specific environment, set the var to empty there (e.g. `.env.local`).

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;
const CRISP_WEBSITE_ID = import.meta.env.VITE_CRISP_WEBSITE_ID;
const GA4_ID = import.meta.env.VITE_GA4_ID;

// Only load trackers in a production build. `import.meta.env.PROD` is
// false under `vite dev`, so local development never pings Meta/GA/Crisp
// even if an ID leaks into `.env`.
const PROD = import.meta.env.PROD;

// Loaded-once guards. Consent can be granted after page load (the visitor
// clicks "Accept" on the CookieYes banner), so the init functions may be
// invoked again from the consent listener — they must never double-inject.
let metaLoaded = false;
let crispLoaded = false;
let ga4Loaded = false;

/**
 * Bootstrap the Meta Pixel (fbevents.js) and fire the initial PageView.
 * No-op unless this is a production build with VITE_META_PIXEL_ID set.
 */
export function initMetaPixel(): void {
  if (!PROD || !META_PIXEL_ID || metaLoaded) return;
  metaLoaded = true;
  /* eslint-disable */
  // Canonical Meta Pixel base snippet, inlined as a function instead of
  // a raw <script> so it's env-gated and tree-shaken out of non-prod.
  (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js',
  );
  /* eslint-enable */
  const fbq = (window as any).fbq;
  fbq('init', META_PIXEL_ID);
  fbq('track', 'PageView');
}

/**
 * Fire a Meta Pixel PageView for an in-app (client-routed) navigation.
 * Safe to call unconditionally — it's a no-op when the pixel wasn't
 * loaded (dev / non-prod / no ID).
 */
export function trackPageView(): void {
  const fbq = (window as any).fbq;
  if (typeof fbq === 'function') fbq('track', 'PageView');
  // GA4 SPA page_view — GA4 only auto-sends the FIRST page_view; client-side
  // route changes need an explicit one. No-op until gtag is loaded (consent).
  const gtag = (window as any).gtag;
  if (typeof gtag === 'function' && GA4_ID) {
    gtag('event', 'page_view', {
      page_path: window.location.pathname + window.location.search,
      page_location: window.location.href,
    });
  }
}

/**
 * Bootstrap Google Analytics 4 (gtag.js). Injected from JS — like the Meta
 * Pixel — rather than hard-coded in <head>, so it's env-gated AND only fires
 * once the visitor has granted the "analytics" consent category (UK PECR: a
 * raw <head> tag would drop analytics cookies before consent). No-op unless
 * this is a production build with VITE_GA4_ID set.
 */
export function initGA4(): void {
  if (!PROD || !GA4_ID || ga4Loaded) return;
  ga4Loaded = true;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  // Match Google's canonical snippet exactly (uses `arguments`, so a real
  // function — not an arrow).
  w.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
  };
  w.gtag('js', new Date());
  w.gtag('config', GA4_ID);
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
}

/**
 * Meta Pixel `InitiateCheckout` — fire when the checkout page/step loads, so
 * checkout drop-off and "started checkout but no Purchase" (abandoned-cart)
 * audiences can be built. `content_ids` are the booked service ids.
 * No-op until the pixel is loaded (consent).
 */
export function trackInitiateCheckout(params: {
  value: number;
  currency?: string;
  contentIds?: string[];
}): void {
  const fbq = (window as any).fbq;
  if (typeof fbq !== 'function') return;
  fbq('track', 'InitiateCheckout', {
    value: Number(params.value) || 0,
    currency: params.currency ?? 'GBP',
    content_ids: params.contentIds ?? [],
    content_type: 'product',
  });
  // GA4 equivalent for the same funnel step (bonus — GA4 is being set up too).
  const gtag = (window as any).gtag;
  if (typeof gtag === 'function' && GA4_ID) {
    gtag('event', 'begin_checkout', {
      value: Number(params.value) || 0,
      currency: params.currency ?? 'GBP',
      items: (params.contentIds ?? []).map((id) => ({ item_id: id })),
    });
  }
}

/**
 * Meta Pixel `Purchase` (+ GA4 `purchase`) — the conversion event. Fire once
 * payment is confirmed, i.e. on the post-Stripe confirmation screen.
 *
 * DEDUPED: the confirmation page is a plain URL the customer can refresh,
 * bookmark or hit "back" onto. Without a guard every reload would report the
 * same revenue again and inflate ROAS. We key on the Stripe session id, which
 * is unique per payment and stable across reloads, and persist it so a new
 * tab doesn't re-fire either.
 *
 * No-op until the pixel / gtag are loaded (consent).
 */
export function trackPurchase(params: {
  value: number;
  currency?: string;
  /** Stripe checkout session id — used as the dedup + transaction key. */
  transactionId: string;
  contentIds?: string[];
}): void {
  if (!params.transactionId) return;
  const key = `cp_purchase_tracked:${params.transactionId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // Storage blocked (private mode / cookies off). Fall through and still
    // report the conversion rather than dropping it entirely — a duplicate
    // is less harmful than a missing sale.
  }

  const fbq = (window as any).fbq;
  if (typeof fbq === 'function') {
    fbq('track', 'Purchase', {
      value: Number(params.value) || 0,
      currency: params.currency ?? 'GBP',
      content_ids: params.contentIds ?? [],
      content_type: 'product',
    });
  }
  const gtag = (window as any).gtag;
  if (typeof gtag === 'function' && GA4_ID) {
    gtag('event', 'purchase', {
      transaction_id: params.transactionId,
      value: Number(params.value) || 0,
      currency: params.currency ?? 'GBP',
      items: (params.contentIds ?? []).map((id) => ({ item_id: id })),
    });
  }
}

/**
 * Load the Crisp chat widget. No-op unless this is a production build
 * with VITE_CRISP_WEBSITE_ID set.
 */
export function initCrisp(): void {
  if (!PROD || !CRISP_WEBSITE_ID || crispLoaded) return;
  crispLoaded = true;
  (window as any).$crisp = [];
  (window as any).CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
  const d = document;
  const s = d.createElement('script');
  s.src = 'https://client.crisp.chat/l.js';
  s.async = true;
  d.getElementsByTagName('head')[0].appendChild(s);
}

// ============================================================
// GDPR consent gating (CookieYes)
// ============================================================
//
// CookieYes can only auto-block trackers that exist as <script> tags in the
// document. Meta Pixel and Crisp are injected from JS above, so CookieYes
// cannot see (or block) them — we must gate them on consent ourselves.
// Without this the banner would be decorative: we'd still drop non-essential
// cookies before the visitor consents, which is exactly what the GDPR /
// PECR rules prohibit.
//
// Category mapping (CookieYes defaults):
//   Google Analytics 4 → "analytics"     (audience / behaviour measurement)
//   Meta Pixel         → "advertisement" (marketing / retargeting pixel)
//   Crisp chat         → "functional"    (not strictly necessary to use the site)
//
// Deny-by-default: if CookieYes never loads (e.g. an ad blocker), consent is
// unknown and NOTHING non-essential fires.

type CkyCategories = {
  necessary?: boolean;
  functional?: boolean;
  analytics?: boolean;
  performance?: boolean;
  advertisement?: boolean;
};

/** Current consent categories, or null when CookieYes hasn't resolved yet. */
function readConsent(): CkyCategories | null {
  const getCkyConsent = (window as any).getCkyConsent;
  if (typeof getCkyConsent !== 'function') return null;
  try {
    return (getCkyConsent()?.categories as CkyCategories) ?? null;
  } catch {
    return null;
  }
}

function applyConsent(categories: CkyCategories | null): void {
  if (!categories) return;
  if (categories.analytics) initGA4();
  if (categories.advertisement) initMetaPixel();
  if (categories.functional) initCrisp();
}

/**
 * Single entry point (called from main.tsx). Loads Meta Pixel / Crisp ONLY for
 * the consent categories the visitor has actually granted, and re-checks when
 * they change their choice — so a first-time visitor who clicks "Accept" gets
 * the trackers immediately without a reload. The init functions are guarded so
 * repeated calls never double-inject.
 */
export function initAnalyticsWithConsent(): void {
  if (!PROD) return;
  // Returning visitors already have a stored choice by the time this runs.
  applyConsent(readConsent());
  // First-time visitors (and anyone updating their preferences) land here.
  document.addEventListener('cookieyes_consent_update', (event: Event) => {
    const detail = (event as CustomEvent)?.detail as
      | { categories?: CkyCategories }
      | undefined;
    applyConsent(detail?.categories ?? readConsent());
  });
}
